import type { SignupAttribution } from '@reed/analytics';
import { api, ApiError } from './api';

export type UserType = 'employer' | 'candidate';
export type TerritorySource = 'geoip' | 'user_selected' | 'default';

export const CURRENT_TERMS_VERSION = '2026-01-01';

export interface BaseProfileBody {
  territory: string;
  territory_source: TerritorySource;
  phone: string;
  postcode: string;
  county?: string;
  accept_terms_version: string;
  // Map of optional-consent key (see lib/consents.ts) → granted. The required
  // terms acceptance is carried by accept_terms_version, not here.
  optional_consents: Record<string, boolean>;
}

// organisation details come from the Redis wizard session (saved pre-WorkOS),
// not the request body — the server rejects unknown fields.
export interface EmployerOnboardingBody extends BaseProfileBody {
  role_title?: string;
  // First/last name captured on the profile step (issue #331). Optional on the
  // wire for backward compatibility; the current UI collects both as required.
  first_name?: string;
  last_name?: string;
  // GA4 attribution (#1458): campaign values, GA ids and the Cookiebot decision
  // at the moment of submission. Omitted when analytics is off.
  attribution?: SignupAttribution;
}

export interface UserPayload {
  id: string;
  org_id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
}

export interface OnboardingResponse {
  token: string;
  user: UserPayload;
}

export interface TerritoryDerivation {
  territory: string;
  territory_source: TerritorySource;
  ip_resolved_country: string;
}

const CANDIDATE_DASHBOARD_URL =
  (import.meta.env.FRONTEND_CANDIDATE_URL as string | undefined) ?? 'http://localhost:5174';
const RECRUITER_CONSOLE_URL =
  (import.meta.env.FRONTEND_RECRUITER_URL as string | undefined) ?? 'http://localhost:5175';

export function signupAuthorizeURL(userType: UserType, email?: string): string {
  const apiBase = (import.meta.env.FRONTEND_API_URL as string | undefined) ?? '';
  const gatewayOrigin = apiBase.startsWith('http') ? new URL(apiBase).origin : window.location.origin;
  const url = new URL('/api/v1/auth/workos/authorize', gatewayOrigin);
  url.searchParams.set('intent', 'signup');
  url.searchParams.set('user_type', userType);
  url.searchParams.set('return_to', window.location.origin);
  if (email) {
    url.searchParams.set('login_hint', email);
  }
  return url.toString();
}

export function buildOnboardingAuthorizeURL(anonSessionId: string, email?: string): string {
  const apiBase = (import.meta.env.FRONTEND_API_URL as string | undefined) ?? '';
  const gatewayOrigin = apiBase.startsWith('http') ? new URL(apiBase).origin : window.location.origin;
  const url = new URL('/api/v1/auth/workos/authorize', gatewayOrigin);
  // The backend keys the whole provisioning/wizard flow on intent=signup;
  // anon_session_id is what routes us back into the org wizard data.
  url.searchParams.set('intent', 'signup');
  url.searchParams.set('user_type', 'employer');
  url.searchParams.set('anon_session_id', anonSessionId);
  url.searchParams.set('return_to', window.location.origin);
  if (email) {
    url.searchParams.set('login_hint', email);
  }
  return url.toString();
}

export type OrgType = 'employer' | 'charity';

export interface OrgWizardData {
  orgType: OrgType;
  registrationNumber: string;
  legalName: string;
  tradingName: string;
  countryCode: string;
}

export interface CompanySearchResult {
  registration_number: string;
  legal_name: string;
  address: string;
  org_type: string;
  status: string;
}

export function searchCompanies(q: string): Promise<CompanySearchResult[]> {
  return api
    .get<{ results: CompanySearchResult[] }>('/auth/company/search', { params: { q } })
    .then((r) => r.results ?? []);
}

export function searchCharities(q: string): Promise<CompanySearchResult[]> {
  return api
    .get<{ results: CompanySearchResult[] }>('/auth/charity/search', { params: { q } })
    .then((r) => r.results ?? []);
}

export function checkOrganisationExists(registrationNumber: string): Promise<boolean> {
  return api
    .get<{ exists: boolean }>('/auth/organisation/exists', {
      params: { registration_number: registrationNumber },
    })
    .then((r) => r.exists);
}

// confirmOrganisationAccess completes the double-opt-in for a scope-b request:
// the token was emailed to the requester and rides in the confirm page's URL
// fragment (never the query string), so it is POSTed in the body — keeping it
// out of server, gateway, and referrer logs. On success the org's admins are
// notified; the call is idempotent for a replayed link.
export function confirmOrganisationAccess(token: string): Promise<{ status: string }> {
  return api.post<{ status: string }>('/auth/organisation/request-access/confirm', { token });
}

// requestOrganisationAccess opens a scope-b double-opt-in "request access"
// from the blocked org-exists banner. The response is constant-shape
// ({status:'check_email'}) whether or not the org exists, so the caller must
// never infer org existence from it. A confirm link is emailed to the requester;
// only on confirm are the org's admins notified.
export function requestOrganisationAccess(body: {
  registration_number: string;
  name: string;
  email: string;
}): Promise<{ status: string }> {
  return api.post<{ status: string }>('/auth/organisation/request-access', body);
}

// Rollout safety (#1458): an auth pod predating attribution rejects the field
// (DisallowUnknownFields -> 400 INVALID_REQUEST), and one that finds a value
// invalid answers 400 INVALID_FIELD naming it. Attribution must never block
// onboarding, so those writes are retried once without it.
function isAttributionRejection(err: unknown): boolean {
  const code = extractApiErrorCode(err);
  return code === 'INVALID_REQUEST' || (code === 'INVALID_FIELD' && extractApiErrorMessage(err, '').startsWith('attribution'));
}

async function sendWithoutRejectedAttribution<T>(
  body: Record<string, unknown>,
  send: (body: Record<string, unknown>) => Promise<T>,
): Promise<T> {
  try {
    return await send(body);
  } catch (err) {
    if (!('attribution' in body) || !isAttributionRejection(err)) {
      throw err;
    }
    const { attribution: _rejected, ...withoutAttribution } = body;
    return send(withoutAttribution);
  }
}

export function putAnonSession(anonSessionId: string, step: string, data: Record<string, unknown>): Promise<void> {
  return sendWithoutRejectedAttribution(
    { anon_session_id: anonSessionId, step, ...data },
    (body) => api.put<void>('/auth/onboarding/session/anon', body),
  );
}

// putAnonAttribution refreshes only the attribution of the anonymous session,
// just before the WorkOS redirect: consent or GA's cookie may have arrived
// after the company step. The server keeps first-touch values. Best-effort.
export function putAnonAttribution(anonSessionId: string, attribution: SignupAttribution, signal?: AbortSignal): Promise<void> {
  return api.put<void>(
    '/auth/onboarding/session/anon',
    { anon_session_id: anonSessionId, step: 'attribution', attribution },
    signal ? { signal } : undefined,
  );
}

export interface AuthWizardSession {
  org_type?: string;
  registration_number?: string;
  legal_name?: string;
  country_code?: string;
  [key: string]: unknown;
}

// getAuthSession reads the authenticated onboarding wizard session
// (onboarding_session:{party_id}) using the onboarding-purpose JWT. Rejects with
// an ApiError (404) when no session has been stored yet.
export function getAuthSession(token: string): Promise<AuthWizardSession> {
  return api.get<AuthWizardSession>('/auth/onboarding/session', {
    headers: { Authorization: `Bearer ${token}` },
  });
}

// putAuthSession stores/merges typed wizard step data into the authenticated
// onboarding session — used by employers who reached onboarding via a
// WorkOS-hosted sign-up and therefore have no pre-WorkOS anon wizard data.
export function putAuthSession(token: string, step: string, data: Record<string, unknown>): Promise<void> {
  return sendWithoutRejectedAttribution({ step, ...data }, (body) =>
    api.put<void>('/auth/onboarding/session', body, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
}

export function dashboardURLFor(userType: UserType): string {
  return userType === 'employer' ? RECRUITER_CONSOLE_URL : CANDIDATE_DASHBOARD_URL;
}

export function deriveTerritory(): Promise<TerritoryDerivation> {
  return api.get<TerritoryDerivation>('/auth/territory/derive');
}

export function completeCandidate(body: BaseProfileBody, token: string): Promise<OnboardingResponse> {
  return api.post<OnboardingResponse>('/auth/onboarding/candidate', body, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function completeEmployer(body: EmployerOnboardingBody, token: string): Promise<OnboardingResponse> {
  const headers = { Authorization: `Bearer ${token}` };
  try {
    return await api.post<OnboardingResponse>('/auth/onboarding/employer', body, { headers });
  } catch (err) {
    // Attribution first (#1458): it is the newest field, and dropping it costs
    // only analytics. A remaining rejection falls through to the names retry.
    if (body.attribution && isAttributionRejection(err)) {
      const retryBody: EmployerOnboardingBody = { ...body };
      delete retryBody.attribution;
      return completeEmployer(retryBody, token);
    }
    // Rollout safety (#331): an auth pod predating the name-capture change
    // rejects the new first_name/last_name fields via DisallowUnknownFields ->
    // 400 INVALID_REQUEST. If the two services aren't rolled out auth-first,
    // retry once WITHOUT the names so signup still completes (the name is
    // captured once auth is updated) instead of a hard signup outage. Any
    // other error is re-thrown unchanged.
    if ((body.first_name || body.last_name) && extractApiErrorCode(err) === 'INVALID_REQUEST') {
      const retryBody: EmployerOnboardingBody = { ...body };
      delete retryBody.first_name;
      delete retryBody.last_name;
      return api.post<OnboardingResponse>('/auth/onboarding/employer', retryBody, { headers });
    }
    throw err;
  }
}

export type RecoverActionKind = 'login_help' | 'reset_password';

export function recoverAccount(
  email: string,
  action: RecoverActionKind = 'login_help',
): Promise<{ status: string }> {
  return api.post<{ status: string }>('/auth/recover', { email, action });
}

export function resendVerification(email: string, token?: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(
    '/auth/resend-verification',
    { email },
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
  );
}

// extractApiErrorMessage reads the message from the { error: { code,
// message } } envelope returned by the auth service.
export function extractApiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiError) {
    const data = err.data as { error?: { message?: string } } | null;
    return data?.error?.message ?? err.message;
  }
  return fallback;
}

// extractApiErrorCode reads the machine-readable code from the
// { error: { code, message } } envelope. Callers branch the invite redeem
// state machine on it (AUTHENTICATION_REQUIRED, PROFILE_REQUIRED, …).
export function extractApiErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    const data = err.data as { error?: { code?: string } } | null;
    return data?.error?.code ?? undefined;
  }
  return undefined;
}

// ── Organisation invite redeem (public /join) ─────────────────────────────────

export interface InvitePreviewResponse {
  invite_id: string;
  organisation_name: string;
  invited_email_masked: string;
  role_type: string;
  expires_at: string;
}

export interface AcceptOrganisationInviteBody {
  invite_token: string;
  // Short-lived invite_identity JWT minted by the WorkOS intent=invite round-trip.
  // Omitted when redeeming with an existing session (the auth cookie).
  identity_token?: string;
  // Base profile — only required when the invitee is a brand-new pending party.
  profile?: BaseProfileBody;
  role_title?: string;
}

// previewOrganisationInvite validates the bearer invite JWT and returns a masked,
// non-sensitive summary of the invite. It also sets the af_invite_handoff cookie
// server-side, gating the subsequent WorkOS intent=invite round-trip.
export function previewOrganisationInvite(inviteToken: string): Promise<InvitePreviewResponse> {
  return api.post<InvitePreviewResponse>('/auth/invites/preview', { invite_token: inviteToken });
}

// acceptOrganisationInvite redeems the invite into its existing organisation and,
// on success, returns the normal session token/user (the auth cookie is also set).
export function acceptOrganisationInvite(body: AcceptOrganisationInviteBody): Promise<OnboardingResponse> {
  return api.post<OnboardingResponse>('/auth/invites/accept', body);
}

// buildInviteAuthorizeURL builds the WorkOS authorize URL for intent=invite. The
// invite JWT is NEVER included — only the opaque invite_id travels here; the
// bearer capability rides in the fragment on /join and the handoff cookie gates
// this round-trip.
export function buildInviteAuthorizeURL(inviteId: string, emailHint?: string): string {
  const apiBase = (import.meta.env.FRONTEND_API_URL as string | undefined) ?? '';
  const gatewayOrigin = apiBase.startsWith('http') ? new URL(apiBase).origin : window.location.origin;
  const url = new URL('/api/v1/auth/workos/authorize', gatewayOrigin);
  url.searchParams.set('intent', 'invite');
  url.searchParams.set('invite_id', inviteId);
  url.searchParams.set('return_to', window.location.origin);
  if (emailHint) {
    url.searchParams.set('login_hint', emailHint);
  }
  return url.toString();
}
